import { useEffect, useState } from "react";
import { Mail, MessageCircle, BellRing, CheckCircle2, Clock, XCircle, RefreshCw, Send, Smartphone, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const KIND_LABELS = {
  received: "Pedido recebido",
  confirmado: "Pedido confirmado",
  em_separacao: "Em separação",
  saiu_para_entrega: "Saiu para entrega",
  entregue: "Pedido entregue",
  cancelamento_solicitado: "Cancelamento solicitado",
  cancelado: "Pedido cancelado",
  webhook: "Webhook (CRM)",
  teste: "SMS de teste",
};

const CHANNEL_META = {
  email: ["E-mail", Mail],
  email_admin: ["E-mail (equipe)", BellRing],
  sms: ["SMS", Smartphone],
  whatsapp: ["WhatsApp", MessageCircle],
  webhook: ["Webhook", Send],
};

const KIND_LABELS_FULL = { ...KIND_LABELS, novo_pedido: "Alerta de nova venda" };

const STATUS_BADGE = {
  enviado: ["Enviada", "bg-green-50 text-green-700"],
  pendente: ["Pendente", "bg-amber-50 text-amber-700"],
  falha: ["Falha", "bg-red-50 text-red-600"],
};

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

export default function AdminNotifications() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/notifications").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const notif = settings?.notifications || {};
  const emailOn = notif.email_on || {};
  const templates = notif.email_templates || {};
  const channels = notif.channels || {};
  const smsOn = notif.sms_on || {};
  const smsTemplates = notif.sms_templates || {};
  const smsCfg = notif.sms || {};
  const emailCfg = notif.email || {};
  const adminAlerts = notif.admin_alerts || {};
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTesting, setEmailTesting] = useState(false);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { notifications: notif });
      toast.success("Modelos, canais e integração salvos!");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar."));
    } finally {
      setSaving(false);
    }
  };

  const sendEmailTest = async () => {
    setEmailTesting(true);
    try {
      await saveSettings();
      const { data: r } = await api.post("/admin/notifications/email-test", { to: emailTestTo });
      toast.success(`E-mail de teste enviado via ${r.provider === "gmail" ? "Gmail oficial" : "serviço gerenciado"}! Confira a caixa de entrada.`);
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível enviar o e-mail de teste."));
    } finally {
      setEmailTesting(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await saveSettings();
      await api.post("/admin/notifications/sms-test", { phone: testPhone });
      toast.success("SMS de teste enviado de verdade! Confira o celular.");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível enviar o SMS de teste."));
    } finally {
      setTesting(false);
    }
  };

  const setNotif = (patch) => setSettings((s) => ({ ...s, notifications: { ...s.notifications, ...patch } }));

  const resend = async (orderId) => {
    try {
      await api.post(`/admin/orders/${orderId}/notify`);
      toast.success("Reenvio solicitado! Confira o status em instantes.");
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data || !settings) {
    return <div className="animate-pulse h-64 bg-white rounded-xl border border-slate-200" />;
  }

  return (
    <div data-testid="admin-notifications">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Central de Notificações</h1>
      <p className="text-sm text-gray-500 mt-1">
        Acompanhe as confirmações e avisos enviados aos clientes — e o que ainda depende de configuração
      </p>

      {/* status dos canais */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`rounded-xl border-2 p-4 ${data.email_configured ? "bg-green-50/50 border-green-200" : "bg-amber-50 border-amber-200"}`} data-testid="channel-email">
          <p className="flex items-center gap-2 font-semibold text-gray-900 text-sm">
            <Mail className="w-4 h-4 text-brand" /> E-mail
          </p>
          <p className="text-xs mt-1.5 text-gray-600">
            {data.email_provider === "gmail"
              ? "Remetente oficial: HG Móveis <hgmoveis2022@gmail.com> (Gmail)."
              : "Serviço gerenciado ativo. Para sair como hgmoveis2022@gmail.com, configure o Gmail oficial abaixo."}
          </p>
          <label className="mt-2.5 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
            <Switch
              checked={channels.email !== false && notif.email_enabled !== false}
              onCheckedChange={(v) => setNotif({ channels: { ...channels, email: v }, email_enabled: v })}
              data-testid="notif-email-enabled"
            />
            Canal {channels.email !== false && notif.email_enabled !== false ? "ativado" : "desativado"}
          </label>
        </div>
        <div className={`rounded-xl border-2 p-4 ${data.sms_configured ? "bg-green-50/50 border-green-200" : "bg-amber-50 border-amber-200"}`} data-testid="channel-sms">
          <p className="flex items-center gap-2 font-semibold text-gray-900 text-sm">
            <Smartphone className="w-4 h-4 text-violet-600" /> SMS (celular)
          </p>
          <p className="text-xs mt-1.5 text-gray-600">
            {data.sms_configured
              ? "Twilio configurada — envios reais de verdade."
              : "Twilio ainda não configurada — nada é simulado: os SMS ficam pendentes até você salvar as credenciais abaixo."}
          </p>
          {data.sms_balance && !data.sms_balance.error && (
            <p className="text-xs mt-1 text-gray-500" data-testid="sms-balance">
              Saldo Twilio: <strong>{data.sms_balance.currency} {data.sms_balance.balance}</strong>
            </p>
          )}
          <label className="mt-2.5 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
            <Switch
              checked={!!channels.sms}
              onCheckedChange={(v) => setNotif({ channels: { ...channels, sms: v } })}
              data-testid="notif-sms-enabled"
            />
            Canal {channels.sms ? "ativado" : "desativado"}
          </label>
        </div>
        <div className="rounded-xl border-2 border-slate-200 bg-white p-4" data-testid="channel-whatsapp">
          <p className="flex items-center gap-2 font-semibold text-gray-900 text-sm">
            <MessageCircle className="w-4 h-4 text-green-600" /> WhatsApp automático
          </p>
          <p className="text-xs mt-1.5 text-gray-600 leading-relaxed">
            <strong>Ainda não configurado.</strong> O envio automático exige a API oficial do WhatsApp Business
            (Meta) ou o Kommo CRM — ambos precisam de credenciais da sua conta e podem ter custo por mensagem.
            Nenhuma automação não oficial será usada, para proteger seu número comercial.
          </p>
        </div>
        <div className={`rounded-xl border-2 p-4 ${data.webhook_enabled ? "bg-green-50/50 border-green-200" : "bg-white border-slate-200"}`} data-testid="channel-webhook">
          <p className="flex items-center gap-2 font-semibold text-gray-900 text-sm">
            <Send className="w-4 h-4 text-slate-500" /> Webhook (Kommo/CRM)
          </p>
          <p className="text-xs mt-1.5 text-gray-600">
            {data.webhook_enabled
              ? "Ativo — cada pedido novo é enviado ao endereço configurado."
              : "Desativado — configure em Site e Textos → Integrações para enviar pedidos ao seu CRM."}
          </p>
        </div>
      </div>

      {/* alertas de novas vendas para a equipe */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-alerts">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
              <BellRing className="w-4 h-4 text-brand" /> Alertas de novas vendas (equipe)
            </h2>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Assim que um pedido é salvo, a equipe recebe um e-mail com todos os detalhes e o botão
              "Ver pedido no painel". É separado dos avisos que os clientes recebem e enviado uma única vez por pedido.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer shrink-0">
            <Switch
              checked={adminAlerts.enabled !== false}
              onCheckedChange={(v) => setNotif({ admin_alerts: { ...adminAlerts, enabled: v } })}
              data-testid="admin-alerts-enabled"
            />
            {adminAlerts.enabled !== false ? "Ativados" : "Desativados"}
          </label>
        </div>
        <div className="mt-4">
          <Label>E-mails da equipe que recebem o alerta (um por linha)</Label>
          <Textarea
            rows={3}
            value={(adminAlerts.emails || []).join("\n")}
            onChange={(e) =>
              setNotif({ admin_alerts: { ...adminAlerts, emails: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) } })
            }
            placeholder={"hgmoveis2022@gmail.com\nvendas@hgmoveis.com.br"}
            className="mt-1.5 text-sm"
            data-testid="admin-alerts-emails"
          />
          <p className="text-[11px] text-slate-400 mt-1">O envio usa o provedor de e-mail escolhido acima (Gmail oficial ou serviço gerenciado).</p>
        </div>
      </div>

      {/* configuração do e-mail oficial (Gmail da loja) */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="email-integration">
        <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-4 h-4 text-brand" /> Remetente oficial dos e-mails
        </h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          As confirmações são enviadas automaticamente assim que o pedido é salvo. Escolha o provedor:
          o <strong>serviço gerenciado</strong> (já funciona, remetente da plataforma) ou o
          <strong> Gmail oficial da loja</strong> — para o cliente receber de <strong>HG Móveis &lt;hgmoveis2022@gmail.com&gt;</strong>.
        </p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="email-provider-choice">
          {[["emergent", "Serviço gerenciado (padrão)"], ["gmail", "Gmail oficial hgmoveis2022@gmail.com"]].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setNotif({ email: { ...emailCfg, provider: id } })}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                (emailCfg.provider || "emergent") === id ? "bg-brand text-white" : "bg-gray-50 text-gray-600 border border-gray-200"
              }`}
              data-testid={`email-provider-${id}`}
            >
              {label}
            </button>
          ))}
        </div>
        {emailCfg.provider === "gmail" && (
          <div className="mt-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed" data-testid="gmail-limits-note">
              <strong>Antes de ativar — limitações do Gmail gratuito:</strong> o Google limita a conta a cerca de
              <strong> 500 e-mails por dia</strong> (acima disso o envio pausa por até 24h). Para o volume da loja isso é
              suficiente; se crescer muito, o ideal é migrar para um domínio próprio verificado. Nunca usamos sua senha
              principal: o envio usa uma <strong>senha de app</strong> do Google, que você pode revogar quando quiser.
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 leading-relaxed">
              <strong>Como gerar a senha de app (2 minutos):</strong> 1) Entre na conta hgmoveis2022@gmail.com e ative a
              <strong> verificação em duas etapas</strong>; 2) Acesse <strong>myaccount.google.com/apppasswords</strong>;
              3) Crie uma senha chamada "HG Móveis Site" e cole aqui os 16 caracteres (sem espaços).
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>E-mail remetente</Label>
                <Input value={emailCfg.gmail_user || ""} disabled className="mt-1.5 bg-gray-50" data-testid="gmail-user" />
              </div>
              <div>
                <Label>Nome do remetente</Label>
                <Input
                  value={emailCfg.sender_name || ""}
                  onChange={(e) => setNotif({ email: { ...emailCfg, sender_name: e.target.value } })}
                  placeholder="HG Móveis"
                  className="mt-1.5"
                  data-testid="gmail-sender-name"
                />
              </div>
              <div>
                <Label>Senha de app do Gmail (16 caracteres)</Label>
                <Input
                  type="password"
                  value={emailCfg.gmail_app_password || ""}
                  onChange={(e) => setNotif({ email: { ...emailCfg, gmail_app_password: e.target.value.replace(/\s/g, "") } })}
                  placeholder="xxxx xxxx xxxx xxxx"
                  className="mt-1.5"
                  data-testid="gmail-app-password"
                />
                <p className="text-[11px] text-slate-400 mt-1">Guardada com segurança; na tela aparece sempre mascarada.</p>
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <div className="flex-1">
                  <Label>E-mail para teste de recebimento</Label>
                  <Input
                    value={emailTestTo}
                    onChange={(e) => setEmailTestTo(e.target.value)}
                    placeholder="seuemail@exemplo.com"
                    className="mt-1.5"
                    data-testid="email-test-to"
                  />
                </div>
                <Button variant="outline" onClick={sendEmailTest} disabled={emailTesting || !emailTestTo.trim()} data-testid="email-test-send">
                  <Mail className="w-4 h-4" /> {emailTesting ? "Enviando..." : "Enviar teste real"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {emailCfg.provider !== "gmail" && (
          <div className="mt-4 flex items-end gap-2">
            <div className="flex-1 max-w-sm">
              <Label>E-mail para teste de recebimento</Label>
              <Input
                value={emailTestTo}
                onChange={(e) => setEmailTestTo(e.target.value)}
                placeholder="seuemail@exemplo.com"
                className="mt-1.5"
                data-testid="email-test-to"
              />
            </div>
            <Button variant="outline" onClick={sendEmailTest} disabled={emailTesting || !emailTestTo.trim()} data-testid="email-test-send">
              <Mail className="w-4 h-4" /> {emailTesting ? "Enviando..." : "Enviar teste real"}
            </Button>
          </div>
        )}
      </div>

      {/* configuração da integração SMS (Twilio) */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="sms-integration">
        <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-violet-600" /> Integração SMS — Twilio
        </h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Crie uma conta em <strong>twilio.com</strong>, compre um número com SMS (EUA) ou use um número verificado,
          e cole aqui o <strong>Account SID</strong>, o <strong>Auth Token</strong> e o número remetente no formato +1...
          O envio para celulares brasileiros é feito no formato +55DDDNUMERO automaticamente. Custos: cobrados pela
          Twilio por mensagem (o saldo aparece no cartão acima quando configurado).
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Account SID</Label>
            <Input
              value={smsCfg.account_sid || ""}
              onChange={(e) => setNotif({ sms: { ...smsCfg, account_sid: e.target.value.trim() } })}
              placeholder="ACxxxxxxxxxxxxxxxx"
              className="mt-1.5"
              data-testid="sms-account-sid"
            />
          </div>
          <div>
            <Label>Auth Token</Label>
            <Input
              type="password"
              value={smsCfg.auth_token || ""}
              onChange={(e) => setNotif({ sms: { ...smsCfg, auth_token: e.target.value.trim() } })}
              placeholder="Token secreto da Twilio"
              className="mt-1.5"
              data-testid="sms-auth-token"
            />
            <p className="text-[11px] text-slate-400 mt-1">Guardado com segurança; na tela aparece sempre mascarado.</p>
          </div>
          <div>
            <Label>Número remetente (Twilio, formato +1...)</Label>
            <Input
              value={smsCfg.from_number || ""}
              onChange={(e) => setNotif({ sms: { ...smsCfg, from_number: e.target.value.trim() } })}
              placeholder="+15551234567"
              className="mt-1.5"
              data-testid="sms-from-number"
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <Switch
                checked={!!smsCfg.enabled}
                onCheckedChange={(v) => setNotif({ sms: { ...smsCfg, enabled: v } })}
                data-testid="sms-enabled"
              />
              Envio de SMS {smsCfg.enabled ? "ATIVO" : "desativado"}
            </label>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label>Celular para SMS de teste (com DDD)</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="21 99999-9999"
              className="mt-1.5"
              data-testid="sms-test-phone"
            />
          </div>
          <Button variant="outline" onClick={sendTest} disabled={testing || !testPhone.trim()} data-testid="sms-test-send">
            <Smartphone className="w-4 h-4" /> {testing ? "Enviando..." : "Enviar SMS de teste real"}
          </Button>
        </div>
      </div>

      {/* contadores */}
      <div className="mt-6 grid grid-cols-3 gap-3" data-testid="notif-counts">
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto" />
          <p className="font-heading font-bold text-2xl text-gray-900 mt-1" data-testid="count-sent">{data.counts.enviadas}</p>
          <p className="text-xs text-slate-500">Enviadas</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <Clock className="w-5 h-5 text-amber-500 mx-auto" />
          <p className="font-heading font-bold text-2xl text-gray-900 mt-1" data-testid="count-pending">{data.counts.pendentes}</p>
          <p className="text-xs text-slate-500">Pendentes</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
          <p className="font-heading font-bold text-2xl text-gray-900 mt-1" data-testid="count-failed">{data.counts.falhas}</p>
          <p className="text-xs text-slate-500">Falhas</p>
        </div>
      </div>

      {/* log */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="font-heading font-semibold text-gray-900">Histórico de envios</h2>
          <Button variant="outline" size="sm" onClick={load} data-testid="notif-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Atualizar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="p-3.5">Data/hora</th>
                <th className="p-3.5">Pedido</th>
                <th className="p-3.5">Canal</th>
                <th className="p-3.5">Tipo</th>
                <th className="p-3.5">Status</th>
                <th className="p-3.5">Detalhe</th>
                <th className="p-3.5 text-right">Reenvio</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-slate-400">Nenhuma notificação registrada ainda.</td></tr>
              ) : (
                data.items.map((n) => {
                  const badge = STATUS_BADGE[n.status] || [n.status, "bg-slate-100 text-slate-600"];
                  return (
                    <tr key={n.id} className="border-b border-slate-50" data-testid={`notif-row-${n.id}`}>
                      <td className="p-3.5 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(n.at)}</td>
                      <td className="p-3.5 font-semibold text-gray-800">{n.order_number || "—"}</td>
                      <td className="p-3.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
                          {(() => { const M = CHANNEL_META[n.channel] || [n.channel || "—", Send]; const I = M[1]; return (<><I className="w-3.5 h-3.5" />{M[0]}</>); })()}
                        </span>
                      </td>
                      <td className="p-3.5 text-xs text-slate-600">{KIND_LABELS_FULL[n.kind] || n.kind || "—"}</td>
                      <td className="p-3.5">
                        <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${badge[1]}`} data-testid={`notif-status-${n.id}`}>
                          {badge[0]}
                        </span>
                      </td>
                      <td className="p-3.5 text-xs text-slate-400 max-w-[220px] truncate" title={n.detail}>{n.detail || "—"}</td>
                      <td className="p-3.5 text-right">
                        {n.status !== "enviado" && n.order_id && (
                          <Button variant="outline" size="sm" onClick={() => resend(n.order_id)} data-testid={`notif-resend-${n.id}`}>
                            <BellRing className="w-3.5 h-3.5" /> Reenviar
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* modelos editáveis por status e canal */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="notif-templates">
        <h2 className="font-heading font-semibold text-gray-900">Avisos por status — canais e modelos</h2>
        <p className="text-xs text-slate-500 mt-1">
          Para cada etapa do pedido, escolha os canais (e-mail e/ou SMS) e edite os textos. Variáveis: {"{nome}"}, {"{numero}"}, {"{total}"}, {"{pagamento}"}, {"{data}"}.
          SMS deve ser curto e objetivo (ideal: até 160 caracteres). Vazio = usa o texto padrão.
        </p>
        <div className="mt-4 space-y-4">
          {Object.entries(KIND_LABELS).filter(([k]) => !["webhook", "teste"].includes(k)).map(([k, label]) => (
            <div key={k} className="border border-slate-100 rounded-lg p-3.5">
              <p className="text-sm font-semibold text-gray-800">{label}</p>
              <div className="mt-2 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer">
                    <Switch
                      checked={emailOn[k] !== false}
                      onCheckedChange={(v) => setNotif({ email_on: { ...emailOn, [k]: v } })}
                      data-testid={`notif-toggle-${k}`}
                    />
                    <Mail className="w-3.5 h-3.5 text-brand" /> E-mail
                  </label>
                  <Textarea
                    rows={k === "received" ? 5 : 2}
                    value={templates[k] || ""}
                    onChange={(e) => setNotif({ email_templates: { ...templates, [k]: e.target.value } })}
                    placeholder="Texto padrão do sistema"
                    className="mt-2 text-sm"
                    data-testid={`notif-template-${k}`}
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer">
                    <Switch
                      checked={smsOn[k] !== false}
                      onCheckedChange={(v) => setNotif({ sms_on: { ...smsOn, [k]: v } })}
                      data-testid={`sms-toggle-${k}`}
                    />
                    <Smartphone className="w-3.5 h-3.5 text-violet-600" /> SMS
                  </label>
                  <Textarea
                    rows={2}
                    maxLength={600}
                    value={smsTemplates[k] || ""}
                    onChange={(e) => setNotif({ sms_templates: { ...smsTemplates, [k]: e.target.value } })}
                    placeholder="Texto curto padrão do sistema"
                    className="mt-2 text-sm"
                    data-testid={`sms-template-${k}`}
                  />
                  <p className={`text-[11px] mt-1 ${(smsTemplates[k] || "").length > 160 ? "text-amber-600 font-medium" : "text-slate-400"}`} data-testid={`sms-count-${k}`}>
                    {(smsTemplates[k] || "").length}/160 caracteres ideais
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button onClick={saveSettings} disabled={saving} className="mt-4 rounded-full" data-testid="notif-save">
          {saving ? "Salvando..." : "Salvar canais, modelos e integração"}
        </Button>
      </div>
    </div>
  );
}
